import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRightIcon, BookOpenIcon, SparklesIcon, HeartIcon, LightBulbIcon } from '@heroicons/react/24/outline';

const Home = () => {
  const programmes = [
    {
      title: "Early Years",
      description: "Play based learning with strong values",
      image: require('../../assets/update/early-years.png'),
      color: "from-amber-400 to-orange-400"
    },
    {
      title: "Primary Years",
      description: "Hifz & Academics in harmony",
      image: require('../../assets/update/primary.png'),
      color: "from-amber-400 to-orange-400"
    },
    {
      title: "School Activities",
      description: "Robotics, Art, Archery",
      image: require('../../assets/update/activities.png'),
      color: "from-amber-400 to-orange-400"
    },
    {
      title: "Qur'an Learning",
      description: "Nazra, Hifz & understanding",
      image: require('../../assets/update/Quran.PNG'),
      color: "from-amber-400 to-orange-400"
    }
  ];

  const uniqueFeatures = [
    {
      title: "Cambridge Curriculum + Islamic Integration",
      description: "Globally respected academic programme while staying connected to Islamic root.",
      icon: LightBulbIcon
    },
    {
      title: "Hifz without Academic Compromise",
      description: "Optional Hifz programme alongside core academics from Grade 3.",
      icon: BookOpenIcon
    },
    {
      title: "Outdoor Learning, Real Life Skills, STEM",
      description: "Practical learning experiences that prepare students for real-world challenges.",
      icon: SparklesIcon
    },
    {
      title: "A nurturing environment rooted in faith",
      description: "Safe, supportive space where Islamic values guide every interaction.",
      icon: HeartIcon
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex items-center justify-center min-h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={require('../../assets/update/home-hero.png')}
            alt="Aureus Cambridge School students"
            className="object-cover w-full h-full"
          />
        </div>
        <div className="absolute inset-0 bg-black/60" />

        <div className="container relative z-10 flex flex-col items-center justify-center px-5 mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-4 text-5xl font-extrabold leading-tight text-white md:text-7xl drop-shadow-lg"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            AUREUS
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-6 text-base font-semibold tracking-widest text-white uppercase md:text-lg"
            style={{ letterSpacing: '0.18em' }}
          >
            Excellence In Both Worlds
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="max-w-3xl mb-8 text-lg leading-relaxed text-white md:text-2xl drop-shadow"
          >
            A modern Cambridge curriculum guided by Islamic values, nurturing
            confident learners for success in both worlds.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col items-center justify-center w-full max-w-md gap-4 sm:flex-row sm:max-w-none"
          >
            <Link
              to="/apply-online"
              className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-black no-underline transition-all duration-300 bg-yellow-400 rounded-full shadow-lg sm:w-auto hover:bg-yellow-500 group"
            >
              Apply Now
              <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="mb-6 text-3xl font-bold md:text-4xl text-slate-900">
                About the School
              </h2>
              <p className="mb-6 text-lg leading-relaxed text-slate-600">
                Aureus Cambridge School blends academic excellence with Islamic tarbiyah in a safe and nurturing environment.
              </p>
              <p className="mb-8 text-lg leading-relaxed text-slate-600">
                From Early Years to Primary, our unique approach ensures students thrive intellectually, morally and spiritually.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center justify-center w-full px-6 py-3 text-lg font-semibold text-black no-underline transition-all duration-300 bg-yellow-400 rounded-full sm:w-auto hover:bg-yellow-500 group"
              >
                Learn More About Us
                <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-3xl h-80">
                {/* Background Image */}
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{
                    backgroundImage: `url(${require('../../assets/update/landing-page-about-school.jpeg')})`,
                  }}
                ></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Programmes Section */}
      <section className="py-20 bg-slate-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-3xl font-bold md:text-4xl text-slate-900">
              Our Programmes
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-slate-600">
              Comprehensive education that nurtures every aspect of your child's development
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {programmes.map((programme, index) => (
              <motion.div
                key={programme.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative overflow-hidden transition-all duration-300 bg-white shadow-lg rounded-xl hover:shadow-xl group hover:-translate-y-2"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={programme.image}
                    alt={programme.title}
                    className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                    style={programme.title === "Primary Years" ? { objectPosition: 'center ' } : programme.title === "Qur'an Learning" ? { objectPosition: 'center' } : {}}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="mb-3 text-xl font-bold text-slate-900">
                    {programme.title}
                  </h3>
                  <p className="leading-relaxed text-slate-600">
                    {programme.description}
                  </p>
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 transition-opacity duration-300 opacity-0 group-hover:opacity-100 bg-gradient-to-t from-amber-500/5 to-transparent rounded-xl"></div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Link
              to="/programmes"
              className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-black no-underline transition-all duration-300 bg-yellow-400 rounded-full sm:w-auto hover:bg-yellow-500 group"
            >
              View All Programmes
              <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What Makes Us Unique Section */}
      <section className="py-20 bg-white">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-3xl font-bold md:text-4xl text-slate-900">
              What Makes Us Unique?
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-slate-600">
              Cambridge Curriculum + Islamic Tarbiyah — A perfect blend for holistic education
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2">
            {uniqueFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start p-8 transition-all duration-300 bg-slate-50 rounded-3xl hover:shadow-lg group"
              >
                <div className="flex items-center justify-center flex-shrink-0 w-16 h-16 mr-8 bg-yellow-400 shadow-md rounded-xl">
                  <feature.icon className="w-8 h-8 text-black" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-4 text-xl font-bold text-slate-900">
                    {feature.title}
                  </h3>
                  <p className="leading-relaxed text-slate-600">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Link
              to="/why-aureus"
              className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-black no-underline transition-all duration-300 bg-yellow-400 rounded-full sm:w-auto hover:bg-yellow-500 group"
            >
              Discover Why Aureus
              <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="max-w-4xl px-4 mx-auto sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              {'★★★★★'.split('').map((star, i) => (
                <span key={i} className="text-3xl text-amber-400">{star}</span>
              ))}
            </div>
            <blockquote className="mb-8 text-2xl font-medium leading-relaxed text-white md:text-3xl">
              "A community which guides by truth and thereby establishes justice."
            </blockquote>
            <p className="font-semibold text-amber-400">— Surah Al-A'raf 7:181</p>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-4xl px-4 mx-auto text-center sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-8 text-3xl font-bold text-black md:text-4xl">
              Ready to begin your child's journey with Aureus?
            </h2>
            <div className="flex flex-col items-center justify-center w-full max-w-md gap-6 sm:flex-row sm:max-w-none">
              <Link
                to="/apply-online"
                className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-white no-underline transition-all duration-300 rounded-full shadow-lg sm:w-auto bg-slate-900 hover:bg-slate-800 group"
              >
                Apply for Admission
                <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+923261116777"
                className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold no-underline transition-all duration-300 border-2 rounded-full sm:w-auto text-slate-900 border-slate-900 hover:bg-slate-900 hover:text-white"
              >
                Contact Us: +92 326 1116777
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 